import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users, DollarSign, Wallet, TrendingUp, Activity, Zap, Target,
  Award, Rocket, Coins, Battery, Banknote, Pickaxe, PlayCircle,
  CheckCircle, XCircle, UserCheck,
} from "lucide-react";
import { getMiningStatsAPI } from "../../api/miningApi";

export default function MiningDashboard() {
  const [statsData, setStatsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);
        const response = await getMiningStatsAPI();
        if (response.success) {
          setStatsData(response.data);
        } else {
          setError("Failed to fetch stats");
        }
      } catch (err) {
        setError("Error fetching stats");
        console.error("Error fetching stats:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const formatNumber = (num, forceDecimals) => {
    if (forceDecimals !== undefined) return num.toFixed(forceDecimals);
    return num.toString();
  };

  const stats = statsData
    ? [
        { title: "Total Users", value: formatNumber(statsData.totalUsers), icon: Users },
        { title: "Active Users", value: formatNumber(statsData.activeUsers), icon: Activity },
        { title: "Inactive Users", value: formatNumber(statsData.inactiveUsers), icon: Users },
        { title: "Active Miners", value: formatNumber(statsData.activeMiners), icon: UserCheck },
        { title: "Active Minings", value: formatNumber(statsData.activeMinings), icon: PlayCircle },
        { title: "Completed Minings", value: formatNumber(statsData.completedMinings), icon: CheckCircle },
        { title: "Cancelled Minings", value: formatNumber(statsData.cancelledMinings), icon: XCircle },
        { title: "Mined Tokens", value: formatNumber(statsData.minedTokens, 0), icon: Coins },
        { title: "Referral Mined Tokens", value: formatNumber(statsData.referralMinedTokens, 0), icon: Award },
        { title: "Balance Tokens", value: formatNumber(statsData.balanceTokens, 0), icon: Wallet },
        { title: "Daily Reward Tokens", value: formatNumber(statsData.dailyRewardTokens), icon: Target },
        { title: "Balance Energy", value: formatNumber(statsData.balanceEnergy, 0), icon: Zap },
        { title: "Login Reward Energy", value: formatNumber(statsData.loginRewardEnergy, 0), icon: Battery },
        { title: "Daily Refill Energy", value: formatNumber(statsData.dailyRefillEnergy, 0), icon: Pickaxe },
        { title: "Total BNB", value: formatNumber(statsData.totalBnb, 5), icon: DollarSign },
        { title: "Balance BNB", value: formatNumber(statsData.balanceBnb, 5), icon: Banknote },
      ]
    : [];

  const quickActions = [
    {
      title: "Manage Users",
      description: "View and edit users",
      icon: Users,
      href: "/mining/active-users",
    },
    {
      title: "View Income",
      description: "Check revenue streams",
      icon: TrendingUp,
      href: "/mining/income",
    },
    {
      title: "Wallet Management",
      description: "Handle transactions",
      icon: Wallet,
      href: "/mining/wallet",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          {/* <div className="p-3 rounded-xl border border-ginox-green/30 bg-ginox-green/10">
            <Rocket className="w-6 h-6 text-ginox-green" />
          </div> */}
          <div>
            <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
              <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
              Mining
            </div>
            <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
              Mining Dashboard <span className="text-gradient-ginox">Overview</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Platform performance at a glance
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          Array.from({ length: 16 }).map((_, index) => (
            <div key={index} className="rounded-lg border border-border/70 bg-card p-5 animate-pulse">
              <p className="h-3 w-24 bg-white/[0.05] rounded mb-3"></p>
              <p className="h-7 w-20 bg-white/[0.05] rounded"></p>
            </div>
          ))
        ) : error ? (
          <div className="col-span-full rounded-lg border border-red-500/20 bg-red-500/5 p-6 text-center">
            <p className="text-red-400 text-sm">Error loading stats: {error}</p>
          </div>
        ) : (
          stats.map((stat, index) => (
            <div key={index} className="rounded-lg border border-border/70 bg-card p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {stat.title}
                </p>
                <stat.icon className="w-4 h-4 text-muted-foreground/50" />
              </div>
              <p className="font-mono text-xl text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground/50 mt-1">all time</p>
            </div>
          ))
        )}
      </div>

      {/* Quick Actions */}
      <div className="border-t border-white/[0.08] pt-5">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-ginox-green" />
          <p className="text-sm font-medium text-foreground">Quick Actions</p>
        </div>
        <p className="text-xs text-muted-foreground mb-5">Common administrative tasks</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.href}
              className="rounded-lg border border-border/70 bg-white/[0.02] p-4 hover:bg-white/[0.04] hover:border-border transition-colors block group"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-md border border-ginox-green/30 bg-ginox-green/10">
                  <action.icon className="w-4 h-4 text-ginox-green" />
                </div>
                <p className="text-sm font-medium text-foreground">{action.title}</p>
              </div>
              <p className="text-xs text-muted-foreground">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
